import { Table, Tag, Typography, Switch } from "antd";
import type { ColumnsType } from "antd/es/table";
import type { CampaignListItem } from "src/api/campaign/campaignApi";
import { ManageBtns } from "src/components/common/TableButtons";
import CustomButton from "src/components/common/CustomButton";

type Props = {
  list: CampaignListItem[];
  loading: boolean;
  disabled?: boolean;
  onEdit: (item: CampaignListItem) => void;
  onDelete: (item: CampaignListItem) => void;
  onStatusChange: (item: CampaignListItem) => void;
  onViewLogs: (campaignId: number) => void;
};

const triggerTypeLabel: Record<string, string> = {
  AUTO: "자동 발급",
  MANUAL: "수동 발급",
  CLAIM: "직접 수령",
  EVENT_ATTENDANCE: "출석체크",
  EVENT_RANDOM: "확률 기반",
};

const displayTypeLabel: Record<string, string> = {
  ROULETTE: "룰렛",
  LOTTERY: "복권",
  SCRATCH: "스크래치",
};

const triggerTypeColor: Record<string, string> = {
  AUTO: "green",
  MANUAL: "blue",
  CLAIM: "cyan",
  EVENT_ATTENDANCE: "purple",
  EVENT_RANDOM: "orange",
};

export default function CampaignTable({
  list,
  loading,
  disabled = false,
  onEdit,
  onDelete,
  onStatusChange,
  onViewLogs,
}: Props) {
  const columns: ColumnsType<CampaignListItem> = [
    {
      title: "ID",
      dataIndex: "campaignId",
      align: "center",
      width: 70,
    },
    {
      title: "캠페인명",
      dataIndex: "campaignName",
      align: "center",
      width: 200,
      render: (value) => <Typography.Text strong>{value}</Typography.Text>,
    },
    {
      title: "트리거 타입",
      dataIndex: "triggerType",
      align: "center",
      width: 130,
      render: (value) => (
        <Tag color={triggerTypeColor[value] || "default"}>{triggerTypeLabel[value] || value}</Tag>
      ),
    },
    {
      title: "표시 타입",
      dataIndex: "displayType",
      align: "center",
      width: 100,
      render: (value) =>
        value ? <Tag color="geekblue">{displayTypeLabel[value] || value}</Tag> : <span>-</span>,
    },
    {
      title: "시작일",
      dataIndex: "campaignStartAt",
      align: "center",
      width: 160,
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
    },
    {
      title: "종료일",
      dataIndex: "campaignEndAt",
      align: "center",
      width: 160,
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
    },
    {
      title: "상시 운영",
      dataIndex: "isAlwaysOn",
      align: "center",
      width: 90,
      render: (value) => (value ? <Tag color="cyan">상시</Tag> : <span>-</span>),
    },
    {
      title: "활성 상태",
      dataIndex: "isActive",
      align: "center",
      width: 100,
      render: (value, record) => (
        <Switch
          checked={value}
          onChange={() => onStatusChange(record)}
          disabled={disabled || loading}
          checkedChildren="활성"
          unCheckedChildren="비활성"
        />
      ),
    },
    {
      title: "생성일",
      dataIndex: "createdAt",
      align: "center",
      width: 180,
      render: (value) => (value ? new Date(value).toLocaleString() : "-"),
    },
    {
      title: "관리",
      dataIndex: "actions",
      width: 220,
      align: "center",
      fixed: "right",
      render: (_, record) => (
        <ManageBtns style={{ justifyContent: "flex-end" }}>
          <CustomButton
            onClick={() => onViewLogs(record.campaignId)}
            disabled={disabled || loading}
            size="small"
          >
            참여 로그
          </CustomButton>
          <CustomButton onClick={() => onEdit(record)} disabled={disabled || loading}>
            수정
          </CustomButton>
          <CustomButton
            customVariant="danger"
            onClick={() => onDelete(record)}
            disabled={disabled || loading}
          >
            삭제
          </CustomButton>
        </ManageBtns>
      ),
    },
  ];

  return (
    <Table
      columns={columns}
      dataSource={list}
      rowKey={(record) => String(record.campaignId)}
      pagination={false}
      loading={loading}
      locale={{ emptyText: "데이터가 없습니다." }}
      scroll={{ x: 1000 }}
    />
  );
}
